import { TitleText, BodyText } from '@policygenius/mortar';
import axios, { isAxiosError } from 'axios';
import decamelizeKeys from 'decamelize-keys';
import { useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { createUseStyles } from 'react-jss';

import { InitialValues } from 'forms/types';

import {
  HEADING,
  SUB_HEADING,
  FREE_QUOTES_COPY,
  FREE_QUOTES_SUBCOPY,
  REVIEW_PLAN_COPY,
  REVIEW_PLAN_SUBCOPY,
  WHY_PG_COPY,
  WHY_PG_SUBCOPY,
} from './constants';
import ApplicationForm from './form';
import { ApplicationFormValues } from './form/types';
import { readEmailFromCache, writeEmailToCache } from './storage';
import buildInitialValues from './utils/buildInitialValues';
import { createValidate } from './utils/validate';

const useStyles = createUseStyles({
  pageContainer: {
    display: 'grid',
    gridTemplateColumns: '76.667% 23.333%',
    gridTemplateAreas: '"content sidebar"',
    background: 'linear-gradient(to bottom, #ECECEC, #FAFAFA)',
    marginTop: '-40px',
    borderBottom: '1px solid #e5e7eb',

    '@media (max-width: 1280px)': {
      gridTemplateColumns: '1fr',
      gridTemplateAreas: '"content"',
      background: '#FFFFFF',
    },
  },
  leftCol: {
    gridArea: 'sidebar',
    backgroundColor: 'transparent',
    padding: '2.5rem 3rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',

    '@media (max-width: 1280px)': { display: 'none' },

    '@media (min-width: 1440px)': {
      '& p': {
        fontSize: '0.9rem',
      },
    },
    '@media (min-width: 1740px)': {
      '& h3, & h2': {
        fontSize: '1.4rem',
      },
      '& p': {
        fontSize: '1.02rem',
      },
    },

    '& h3, & h2': {
      marginTop: 0,
      marginBottom: 8,
      lineHeight: 1.15,
    },
    '& p': {
      color: '#333',
      textAlign: 'justify',
      lineHeight: 1.6,
      hyphens: 'auto',
      marginLeft: '1.25rem',
    },
    '& section:not(:last-child)': { marginBottom: '2rem' },
  },
  rightCol: {
    gridArea: 'content',
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderTopRightRadius: '15px',
    borderBottomRightRadius: '15px',
    padding: '2.5rem 3rem',

    '@media (max-width: 1280px)': {
      padding: '2rem',
      borderRadius: 0,
    },

    '@media (max-width: 767px)': {
      padding: '2rem 1.5rem',
    },
  },

  formWrapper: {
    maxWidth: '570px',
    margin: '0 auto',
    width: '100%',
    marginBottom: '1rem',

    '@media (min-width: 1440px)': {
      maxWidth: '620px',
    },
  },

  heading: {
    textAlign: 'center',
    marginBottom: 8,
    '& h1': {
      marginTop: 0,

      '@media (min-width: 1440px)': {
        fontSize: '2.2rem',
      },
    },
  },

  subHeading: {
    textAlign: 'center',
    marginBottom: '24px',
    fontSize: '1.2rem',
    color: '#6b7280',

    '@media (min-width: 1440px)': {
      fontSize: '1.3rem',
    },
  },
});

const makeAPIRequest = async (endpoint: string, data: any) => {
  try {
    const response = await axios.post(endpoint, data, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });

    return response;
  } catch (error) {
    if (isAxiosError(error)) {
      const errorMessage = error.response?.data || error.message;

      console.error('API error:', error.response?.status, errorMessage);
      throw new Error(
        `API call failed with status ${error.response?.status}: ${JSON.stringify(errorMessage)}`
      );
    }
    throw error;
  }
};

const readApplication = async (email: string) => {
  try {
    const res = await axios.get('/disability-insurance/applications/read_application', {
      params: { email },
      withCredentials: true,
    });

    return res.data;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('read_application error:', error.response?.status, error.response?.data);
    } else {
      console.error('read_application error:', error);
    }

    return null;
  }
};

const Application = () => {
  const onSubmit = async (values: ApplicationFormValues) => {
    try {
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneType: values.phoneType,
        phone: values.phoneNumber,
      };

      await makeAPIRequest(
        '/disability-insurance/applications/create_with_new_flow',
        decamelizeKeys(payload)
      );
      writeEmailToCache(values.email);
      const monolithHost = process.env.REACT_APP_PG_MONOLITH_HOST;
      const redirectUrl = `${monolithHost}/dashboard/disability/confirmation`;

      window.location.assign(redirectUrl);
    } catch (error) {
      console.error('Error submitting application:', error);
    }
  };
  const validate = createValidate();
  const [initialValues, setInitialValues] = useState<InitialValues<ApplicationFormValues>>(
    buildInitialValues({})
  );

  useEffect(() => {
    const email = readEmailFromCache();

    if (!email) {
      return;
    }

    (async () => {
      const server = await readApplication(email);

      if (!server) {
        setInitialValues((prev) => (prev.email ? prev : buildInitialValues({ email })));

        return;
      }

      const mapped: Partial<ApplicationFormValues> = {
        firstName: server.first_name ?? '',
        lastName: server.last_name ?? '',
        email: server.email ?? email,
        phoneType: server.phone_type ?? '',
        phoneNumber: server.phone ?? '',
      };

      setInitialValues(buildInitialValues(mapped));
    })();
  }, []);
  const classes = useStyles();

  return (
    <div className={classes.pageContainer}>
      <div className={classes.leftCol}>
        <section>
          <TitleText as="h3" size="small">
            {WHY_PG_COPY}
          </TitleText>
          <BodyText as="p" weight="regular" size="small">
            {WHY_PG_SUBCOPY}
          </BodyText>
        </section>
        <section>
          <TitleText as="h3" size="small">
            {FREE_QUOTES_COPY}
          </TitleText>
          <BodyText as="p" weight="regular" size="small">
            {FREE_QUOTES_SUBCOPY}
          </BodyText>
        </section>
        <section>
          <TitleText as="h3" size="medium">
            {REVIEW_PLAN_COPY}
          </TitleText>
          <BodyText as="p" weight="regular" size="small">
            {REVIEW_PLAN_SUBCOPY}
          </BodyText>
        </section>
      </div>
      <div className={classes.rightCol}>
        <div className={classes.formWrapper}>
          <div className={classes.heading}>
            <TitleText as="h1" size="xlarge">
              {HEADING}
            </TitleText>
          </div>
          <p className={classes.subHeading}>{SUB_HEADING}</p>
          <Form<ApplicationFormValues, InitialValues<ApplicationFormValues>>
            initialValues={initialValues}
            onSubmit={onSubmit}
            validate={validate}
          >
            {(formProps) => <ApplicationForm {...formProps} />}
          </Form>
        </div>
      </div>
    </div>
  );
};

export default Application;
